import React from "react";
import { useStore } from "../store";
import { LPA_ROUTING, auditorTypeStyle } from "../data/eightDData";
import { RoleGuard } from "../components/RoleGuard";
import { WebhookIntegrationsSection } from "../components/WebhookIntegrationsSection";
import { TmsSettingsCard } from "../components/TmsSettingsCard";
import { SecuritySection } from "../components/SecuritySection";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { ThemeSwitcher } from "../components/ThemeSwitcher";
import { LanguageInlinePicker, DisplayLanguagePicker } from "../components/LanguageSelector";
import { useT } from "../lib/i18n";

const IntegrationCard = ({ title, accent, fields, badge, testId }) => (
  <div
    className="rounded-md p-5 relative overflow-hidden"
    style={{
      background: "#111420",
      border: "1px solid rgba(255,255,255,0.12)",
    }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-4">
      <span
        className="rounded-md flex items-center justify-center"
        style={{
          width: 38,
          height: 38,
          background: `${accent}22`,
          border: `1px solid ${accent}66`,
          color: accent,
          fontFamily: "'DM Mono', monospace",
          fontSize: 14,
          fontWeight: 700,
        }}
      >
        {title.charAt(0)}
      </span>
      <div className="flex-1">
        <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
          {title}
        </div>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            color: "#f3b14a",
            letterSpacing: "0.8px",
            marginTop: 2,
          }}
        >
          {badge}
        </div>
      </div>
      <label
        className="relative inline-flex cursor-not-allowed"
        style={{ opacity: 0.5 }}
      >
        <input type="checkbox" disabled className="sr-only" />
        <span
          style={{
            width: 36,
            height: 20,
            background: "rgba(255,255,255,0.08)",
            borderRadius: 999,
            position: "relative",
            display: "inline-block",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: 2,
              left: 2,
              width: 14,
              height: 14,
              background: "rgba(255,255,255,0.3)",
              borderRadius: "50%",
            }}
          />
        </span>
      </label>
    </div>

    <div className="space-y-2.5">
      {fields.map((f) => (
        <div key={f.label}>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#9198b0",
              textTransform: "uppercase",
            }}
          >
            {f.label}
          </div>
          <input
            disabled
            placeholder={f.placeholder}
            className="mt-1 w-full px-3 py-2 rounded-md"
            style={{
              background: "#0d0f18",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#9198b0",
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              cursor: "not-allowed",
            }}
          />
        </div>
      ))}
    </div>

    <button
      disabled
      className="w-full mt-4 px-3 py-2 rounded-md"
      style={{
        background: `${accent}40`,
        border: `1px solid ${accent}80`,
        color: "#fff",
        fontFamily: "'Syne', system-ui, sans-serif",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.4px",
        cursor: "not-allowed",
        opacity: 0.6,
      }}
    >
      Connect {title}
    </button>

    {/* "Available in Phase 3" overlay tag */}
    <div
      className="absolute top-4 right-14"
      style={{
        background: "rgba(243,156,18,0.13)",
        border: "1px solid rgba(243,156,18,0.45)",
        color: "#f3b14a",
        fontFamily: "'DM Mono', monospace",
        fontSize: 9,
        padding: "2px 8px",
        borderRadius: 999,
        letterSpacing: "0.8px",
      }}
    >
      AVAILABLE IN PHASE 3
    </div>
  </div>
);

export default function Settings() {
  const { t } = useT();
  return (
    <div data-testid="settings-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 34px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            {t("settings.title")}
          </h1>
          <div
            className="mt-1.5 font-dm-mono"
            style={{ fontSize: 11, color: "#c5cad8", letterSpacing: "0.4px" }}
          >
            {t("settings.subtitle")}
          </div>
        </div>
      </div>

      <div
        className="mb-4 mt-2"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {t("settings.account_security")}
      </div>

      <ChangePasswordCard />
      <SecuritySection />

      <div
        className="mb-4 mt-2"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {t("settings.appearance")}
      </div>
      <div
        className="mb-8 rounded-md p-5"
        style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
        data-testid="settings-appearance-section"
      >
        <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
          {t("settings.theme")}
        </div>
        <div className="mt-1 mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
          {t("settings.theme_help")}
        </div>
        <ThemeSwitcher size="lg" showLabels={true} />
        <div className="grid grid-cols-3 gap-3 mt-4" data-testid="settings-theme-previews">
          {[
            { label: "Light", bg: "#F4F6FA", border: "rgba(0,0,0,0.08)", text: "#0D1117", dim: "#4A5068" },
            { label: "Auto",  bg: "linear-gradient(135deg, #F4F6FA 50%, #08090d 50%)", border: "rgba(120,120,120,0.30)", text: "#444", dim: "#666" },
            { label: "Dark",  bg: "#08090d", border: "rgba(255,255,255,0.12)", text: "#f0f2f8", dim: "#9198b0" },
          ].map((p) => (
            <div
              key={p.label}
              className="rounded-md p-3"
              style={{ background: p.bg, border: `1px solid ${p.border}` }}
              data-testid={`settings-theme-preview-${p.label.toLowerCase()}`}
            >
              <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 12, color: p.text }}>{p.label}</div>
              <div className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: p.dim }}>S1 Critical · 3×</div>
              <div className="mt-2 inline-block px-1.5 py-0.5 rounded" style={{ background: "rgba(226,75,74,0.20)", color: "#E24B4A", fontFamily: "'DM Mono', monospace", fontSize: 8, letterSpacing: "0.6px" }}>BREACHING</div>
            </div>
          ))}
        </div>
      </div>

      {/* Iter36 — Language (Level 1 + Level 2) */}
      <div
        className="mb-4 mt-2"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {t("settings.language")}
      </div>
      <div
        className="mb-8 rounded-md p-5"
        style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
        data-testid="settings-language-section"
      >
        <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
          {t("settings.ui_language")}
        </div>
        <div className="mt-1 mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
          {t("settings.ui_language_help")}
        </div>
        <LanguageInlinePicker hasUser={true} testIdPrefix="settings-ui-language" />

        <div className="mt-6" style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />

        <div className="mt-5" style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
          {t("settings.content_language")}
        </div>
        <div className="mt-1 mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8", lineHeight: 1.5 }}>
          {t("settings.content_language_help")}
        </div>
        <DisplayLanguagePicker hasUser={true} testIdPrefix="settings-display-language" />
      </div>

      <div
        className="mb-4 mt-2"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#9198b0",
          textTransform: "uppercase",
        }}
      >
        {t("settings.integrations")}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <IntegrationCard
          testId="settings-jira-card"
          title="Jira"
          accent="#0052CC"
          badge="NOT CONNECTED — BACKEND REQUIRED"
          fields={[
            { label: "Jira URL", placeholder: "https://your-org.atlassian.net" },
            { label: "API Token", placeholder: "ATATT3xFfGF0…" },
            { label: "Project Key", placeholder: "SITROOM" },
          ]}
        />
        <IntegrationCard
          testId="settings-planner-card"
          title="MS Planner"
          accent="#7B5EA7"
          badge="NOT CONNECTED — BACKEND REQUIRED"
          fields={[
            { label: "Microsoft 365 Tenant", placeholder: "your-tenant.onmicrosoft.com" },
            { label: "Client ID", placeholder: "00000000-0000-0000-0000-000000000000" },
            { label: "Default Plan ID", placeholder: "auto-detect after sign-in" },
          ]}
        />
      </div>

      <AuditConfigCard />

      <RoleGuard minRole="tenant_admin">
        <TmsSettingsCard />
      </RoleGuard>

      <RoleGuard minRole="tenant_admin">
        <WebhookIntegrationsSection />
      </RoleGuard>
    </div>
  );
}

const Toggle = ({ checked, onChange, testId }) => (
  <button
    onClick={() => onChange(!checked)}
    data-testid={testId}
    aria-pressed={checked}
    style={{
      width: 36,
      height: 20,
      background: checked ? "rgba(39,174,96,0.5)" : "rgba(255,255,255,0.08)",
      border: `1px solid ${checked ? "rgba(39,174,96,0.6)" : "rgba(255,255,255,0.12)"}`,
      borderRadius: 999,
      position: "relative",
      cursor: "pointer",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 1,
        left: checked ? 17 : 1,
        width: 14,
        height: 14,
        background: "#fff",
        borderRadius: "50%",
        transition: "left 180ms",
      }}
    />
  </button>
);

const AuditConfigCard = () => {
  const autoTriggerLpa = useStore((s) => s.autoTriggerLpa);
  const setAutoTriggerLpa = useStore((s) => s.setAutoTriggerLpa);
  const autoOfferEightD = useStore((s) => s.autoOfferEightD);
  const setAutoOfferEightD = useStore((s) => s.setAutoOfferEightD);

  return (
    <div
      className="mt-4 rounded-md p-5 relative overflow-hidden"
      style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
      data-testid="audit-config-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className="rounded-md flex items-center justify-center"
          style={{
            width: 38, height: 38,
            background: "rgba(142,68,173,0.18)", border: "1px solid rgba(142,68,173,0.55)",
            color: "#c89adb", fontFamily: "'DM Mono', monospace", fontSize: 14, fontWeight: 700,
          }}
        >
          A
        </span>
        <div className="flex-1">
          <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: 16, color: "#f0f2f8" }}>
            Audit Configuration
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              color: "#c5cad8",
              letterSpacing: "0.5px",
              marginTop: 2,
            }}
          >
            LPA routing rules, auditor directory, and auto-offer toggles
          </div>
        </div>
      </div>

      {/* Routing rules */}
      <div className="relative">
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            letterSpacing: "1.4px",
            color: "#9198b0",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          Routing Rules — Standard × Severity → Auditor Type
        </div>
        <div className="overflow-x-auto rounded-md" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'DM Mono', monospace", fontSize: 10 }}>
            <thead>
              <tr style={{ background: "#0d0f18", color: "#9198b0" }}>
                <th className="text-left py-1.5 px-2">STANDARD</th>
                <th className="text-left py-1.5 px-2">S1</th>
                <th className="text-left py-1.5 px-2">S2</th>
                <th className="text-left py-1.5 px-2">S3</th>
                <th className="text-left py-1.5 px-2">S4</th>
              </tr>
            </thead>
            <tbody>
              {["VDA 6.3", "VDA 6.8", "IATF 16949"].map((std) => (
                <tr key={std} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", color: "#cfd1d8" }}>
                  <td className="py-1.5 px-2" style={{ fontWeight: 600 }}>{std}</td>
                  {["s1", "s2", "s3", "s4"].map((sev) => {
                    const t = auditorTypeStyle(LPA_ROUTING[sev].type);
                    return (
                      <td key={sev} className="py-1.5 px-2">
                        <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color, padding: "1px 6px", borderRadius: 3, fontSize: 9 }}>
                          {t.label}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "0.5px" }}>
          Configure (Phase 3) — per-customer / per-part overrides
        </div>
      </div>

      {/* Auditor Directory */}
      <div className="mt-4 rounded-md p-3 relative" style={{ background: "rgba(15,17,23,0.5)", border: "1px dashed rgba(255,255,255,0.08)" }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.4px", textTransform: "uppercase" }}>
          Auditor Directory
        </div>
        <div className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }}>
          Will list internal/customer/third-party auditors with assignment rules.
        </div>
        <span
          className="absolute top-2 right-2"
          style={{
            background: "rgba(243,156,18,0.13)",
            border: "1px solid rgba(243,156,18,0.45)",
            color: "#f3b14a",
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            padding: "2px 8px",
            borderRadius: 999,
            letterSpacing: "0.8px",
          }}
        >
          PHASE 3
        </span>
      </div>

      {/* Toggles */}
      <div className="mt-4 space-y-2.5">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12, color: "#f0f2f8" }}>
              Auto-trigger LPA on S1/S2 incidents
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
              Skip the 'Request Audit?' confirmation toast and create the LPA request immediately.
            </div>
          </div>
          <Toggle checked={autoTriggerLpa} onChange={setAutoTriggerLpa} testId="toggle-auto-lpa" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 600, fontSize: 12, color: "#f0f2f8" }}>
              Auto-offer 8D on S1/S2 incidents
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: "#9198b0" }}>
              Show the 'Open 8D?' toast 1.5s after S1/S2 incident is opened.
            </div>
          </div>
          <Toggle checked={autoOfferEightD} onChange={setAutoOfferEightD} testId="toggle-auto-8d" />
        </div>
      </div>
    </div>
  );
};
